/**
 * PhotoUpload Component
 * 
 * Displays a photo upload interface with:
 * - Passport-style photo outline placeholder
 * - File upload input
 * - Image preview when uploaded
 * - Photo removal capability
 */

import React, { useState, useRef, useEffect } from 'react';
import apiService from '../services/api';
import { Camera, AlertTriangle, X } from 'lucide-react';
import './PhotoUpload.css';

const PhotoUpload = ({
  onPhotoSelect,
  photoUrl,
  onPhotoRemove,
  label = "Upload Photo",
  hint = "Upload a professional headshot (JPG, PNG, max 5MB)",
  required = false
}) => {
  const fileInputRef = useRef(null);
  const [error, setError] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
  const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Reset error
    setError(null);

    // Validate file type
    if (!ALLOWED_TYPES.includes(file.type)) {
      setError('Please upload a JPG, PNG, or WebP image');
      return;
    }

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      setError('File size must be less than 5MB');
      return;
    }

    // Create local preview immediately
    const reader = new FileReader();
    reader.onload = (event) => {
      const dataUrl = event.target.result;
      setPreviewUrl(dataUrl);
    };
    reader.readAsDataURL(file);

    // Notify parent with file object
    if (onPhotoSelect) {
      onPhotoSelect({
        file,
        name: file.name,
        size: file.size,
        type: file.type
      });
    }

    // Reset input so same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Update preview when parent provides a `photoUrl` (e.g., loaded from server)
  useEffect(() => {
    if (!photoUrl) {
      setPreviewUrl(null);
      return;
    }

    // If photoUrl is a relative uploads path, prefix with API base URL when available
    if (photoUrl.startsWith('/uploads') && apiService.baseURL) {
      setPreviewUrl(`${apiService.baseURL}${photoUrl}`);
    } else {
      setPreviewUrl(photoUrl);
    }
  }, [photoUrl]);

  const handleRemovePhoto = (e) => {
    e.preventDefault();
    setPreviewUrl(null);
    setError(null);

    if (onPhotoRemove) {
      onPhotoRemove();
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const file = e.dataTransfer?.files?.[0];
    if (file) {
      // Trigger file input change
      const dataTransfer = new DataTransfer();
      dataTransfer.items.add(file);
      if (fileInputRef.current) {
        fileInputRef.current.files = dataTransfer.files;
        handleFileSelect({ target: { files: dataTransfer.files } });
      }
    }
  };

  return (
    <div className="photo-upload-container">
      <label className="photo-upload-label">
        {label}
        {required && <span className="required">*</span>}
      </label>

      <div className="photo-upload-wrapper">
        {/* Photo Preview Area */}
        <div
          className="photo-frame"
          onDragOver={handleDragOver}
          onDragEnter={handleDragEnter}
          onDrop={handleDrop}
          role="button"
          tabIndex={0}
          onClick={() => fileInputRef.current?.click()}
          onKeyPress={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              fileInputRef.current?.click();
            }
          }}
        >
          {previewUrl ? (
            <div className="photo-preview">
              <img src={previewUrl} alt="User portrait" className="photo-image" />
              <button
                className="photo-remove-btn"
                onClick={handleRemovePhoto}
                type="button"
                aria-label="Remove photo"
              >
                <X size={14} />
              </button>
            </div>
          ) : (
            <div className="photo-placeholder">
              {/* SVG Passport Photo Outline */}
              <svg
                viewBox="0 0 200 250"
                className="passport-outline"
                aria-hidden="true"
              >
                {/* Face outline */}
                <ellipse cx="100" cy="80" rx="45" ry="50" fill="none" stroke="currentColor" strokeWidth="2" />

                {/* Head */}
                <circle cx="100" cy="60" r="40" fill="none" stroke="currentColor" strokeWidth="2" />

                {/* Eyes */}
                <circle cx="85" cy="55" r="4" fill="currentColor" />
                <circle cx="115" cy="55" r="4" fill="currentColor" />

                {/* Nose */}
                <line x1="100" y1="65" x2="100" y2="75" stroke="currentColor" strokeWidth="2" />

                {/* Mouth */}
                <path d="M 85 85 Q 100 90 115 85" fill="none" stroke="currentColor" strokeWidth="2" />

                {/* Shoulders */}
                <path d="M 55 130 L 50 200 L 150 200 L 145 130" fill="none" stroke="currentColor" strokeWidth="2" />

                {/* Center line guides */}
                <line x1="100" y1="10" x2="100" y2="30" stroke="currentColor" strokeWidth="1" strokeDasharray="3,3" opacity="0.5" />
                <line x1="70" y1="120" x2="130" y2="120" stroke="currentColor" strokeWidth="1" strokeDasharray="3,3" opacity="0.5" />
              </svg>

              <div className="placeholder-text">
                <p className="placeholder-icon"><Camera size={24} /></p>
                <p className="placeholder-main">Upload Photo</p>
                <p className="placeholder-sub">Click or drag here</p>
              </div>
            </div>
          )}
        </div>

        {/* Hidden File Input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          onChange={handleFileSelect}
          className="file-input"
          aria-label="Upload photo file"
        />

        {/* Help Text */}
        {!error && hint && (
          <p className="photo-hint">{hint}</p>
        )}

        {/* Error Message */}
        {error && (
          <p className="photo-error" role="alert" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <AlertTriangle size={14} /> {error}
          </p>
        )}
      </div>
    </div>
  );
};

export default PhotoUpload;
